import { Form } from 'antd';
import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import ProductAPI from 'restAPI/ProductAPI';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { MODAL_SIZE_LARGE } from 'constants/Layout';
import Vocabulary from 'components/Vocabulary';
import { FieldTypes } from 'utils/formUtils';
import { IntegrationType } from './common';
import message from "components/CustomMessage";
import BaseModalForm from 'components/BaseModalForm';
import SubProductAPI from 'restAPI/SubProductAPI';
import SubProductEnvironmentTypeAPI from 'restAPI/SubProductEnvironmentTypeAPI';
import { isArray, find, isEmpty, isFunction, cloneDeep } from 'lodash';
import { getEnvSelectOptions } from './new/util';
import { PermissionSetMap } from 'constants/RBACConstants';
import { hasPermission } from 'utils/authService';
import { useHasDisabledEnvironment } from 'contexts/GlobalSettingsContext';

const initialFormValues = {
    configurations: [{
        product: undefined,
        subProduct: undefined,
        environment: undefined,
    }]
};

const DEFAULT_NAME = "<DEFAULT>";

const GenericMappingForm = forwardRef(function GenericMappingForm({ record, loginConfigId, configurations, onUpdateConfigComponent, afterClose, toolName, deleteIconTooltip, onValuesChange, formDefaultValues, toolLoading, disableAddConfiguration = false, ...props }, ref) {
    const [form] = Form.useForm(props.form);
    const editMode = !isEmpty(record);
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [subProductsMap, setSubProductsMap] = useState({});
    const [envMap, setEnvMap] = useState({});
    const disableMapping = editMode && !props.isUnmapped;
    const { parseExtraFieldsDataFromResponse } = props;
    const {hasDisabledEnvironment, envValue} = useHasDisabledEnvironment();
    const modalProps = {
        width: MODAL_SIZE_LARGE,
        style: { top: 80 },
        okButtonProps: {
            disabled: !hasPermission(editMode ? PermissionSetMap.EDIT_TOOL_CONFIG : PermissionSetMap.WRITE_TOOLS)
        },
    }
      
    const fetchSubProductsAndEnvironments = useCallback(async (configurations) => {
        let subProductPromises = [];
        let envPromises = [];
        let subProducts = {};
        let envs = {};
        configurations.forEach(config => {
            subProductPromises.push(SubProductAPI.getAllQuick(config.product || config.product_id).promise);
            if(config.subProductId || config.sub_product_id){
                //fetch environment only when subproduct ID is present
                envPromises.push(SubProductEnvironmentTypeAPI.getAllQuick(config.subProductId || config.sub_product_id).promise);
            }
        });

        Promise.all(subProductPromises).then((result) => {
            result.forEach((entry, i) => {
                const product = configurations[i].product || configurations[i].product_id;
                subProducts[product] = entry;
            }); 
            const defaultSubProduct =
            find(subProducts[configurations[0].product], { name: DEFAULT_NAME })
              ?.name ?? undefined;
            if (!editMode && defaultSubProduct) {
                //setting default subproduct and environment on create mapping
                const clonedConfig = cloneDeep(configurations);
                //fetching environment for Default subproduct
                const defaultSubProductId = find(subProducts[clonedConfig[0].product], { name: DEFAULT_NAME }).id
                SubProductEnvironmentTypeAPI.getAllQuick(defaultSubProductId)
                .promise.then((response) => {
                    const product = clonedConfig[0].product;
                    const subProduct = defaultSubProduct;
                    envs[`${product}-${subProduct}`] = response;

                    clonedConfig[0] = {
                    ...clonedConfig[0],
                    subProduct: defaultSubProduct,
                    environment: find(
                        envs[`${product}-${defaultSubProduct}`],
                        { name: "Production" }
                    )?.name,
                    };
                    //setting default subproduct and environment form values
                    form.setFieldsValue({ configurations: clonedConfig });
                    setEnvMap(envs);
                })
                .catch((err) => console.error(err));
            }
            setSubProductsMap(subProducts);
        }).catch((err) => console.error(err));

        Promise.all(envPromises).then((result) => {
            result.forEach((entry, i) => {
                const product = configurations[i].product || configurations[i].product_id; 
                const subProduct = configurations[i].subProduct || configurations[i].sub_product_name; 
                envs[`${product}-${subProduct}`] = entry;
            });
            setEnvMap(envs);
        }).catch((err) => console.error(err));
    }, [form, editMode]);

    const fetchProductSubproducts = useCallback(() => {
        setLoading(true);
        const { promise, cancel } = ProductAPI.getAllQuick();
        promise.then(async (_products) => {
                setProducts(_products);

            if(!editMode) {
                //set Default Product/SubProd/Env on Create Mapping [ENG-52245]
                //We are not overriding formDefaultValues prop
                const formValues = formDefaultValues ? formDefaultValues : initialFormValues.configurations;
                const clonedFormValues = cloneDeep(formValues);
                const defaultProduct = find(_products, { name: DEFAULT_NAME })?.id;
                if(defaultProduct){
                    clonedFormValues[0].product = defaultProduct
                }
                form.setFieldsValue({ configurations: clonedFormValues });
                // formValues contains default values for 1st record
                // if it contains product, then fetch subproducts
                if(clonedFormValues[0].product){
                    fetchSubProductsAndEnvironments([clonedFormValues[0]]);
                }
                setLoading(false);
            } else {
                if(record.product){
                    fetchSubProductsAndEnvironments([record]);
                }
                const { product_name } = record;
                const _productId = (find(_products, (obj) => obj.name === product_name))?.id;
                const extraFieldsData = isFunction(parseExtraFieldsDataFromResponse) ? parseExtraFieldsDataFromResponse(record) : {};
                form.setFieldsValue({ configurations: [{
                    ...record,
                    ...extraFieldsData,
                    subProduct: record.sub_product_name,
                    product: _productId,
                }]});
                console.log(extraFieldsData)
                setLoading(false);
            }
        }).catch(err => {
            console.error(err);
            setLoading(false);
        });
        return cancel;
    }, [form, editMode, fetchSubProductsAndEnvironments, record, parseExtraFieldsDataFromResponse,formDefaultValues]);

    useEffect(() => {
        return fetchProductSubproducts();
    }, [fetchProductSubproducts]);


    const fetchEnv = (subProductId, envMapKey) => {
        SubProductEnvironmentTypeAPI.getAllQuick(subProductId).promise.then(envs => {
            setEnvMap((envMap) => ({
                ...envMap,
                [envMapKey]: envs
            }));
        }).catch(err => console.error(err))
    }

    const onProductSubProductCreation = async (productSubProductData, field, type) => {
        const {name, id} = productSubProductData
        let newValues = form.getFieldValue("configurations") || {};
        const productId = newValues[field.name]?.product
        newValues[field.name] = {
            ...newValues[field.name],
            ...(type === 'product' ? { product: id, subProduct: undefined } : {}),
            ...(type === 'subProduct' ? { subProduct: name } : {}),
            environment: undefined
        }

        if(type === "product") {
            setProducts(products => ([...products, { id, name }]))
        }

        if(type === 'subProduct') {
            setSubProductsMap((subProductsMap) => ({
                ...subProductsMap,
                [productId]: [...(subProductsMap[productId] || []),{id, name}]
            }));
            fetchEnv(id, `${productId}-${name}`)
        }

        form.setFieldsValue(newValues);
    }

    const onChange = (value, field, type) => {
        const { name } = field;
        let newValues = form.getFieldValue("configurations") || [];
        if (newValues[name]) {
            newValues[name] = {
                ...newValues[name],
                ...(type === 'product' && { subProduct: undefined }),
                environment: undefined
            }

            if(type === 'product' && !subProductsMap[value]) {
                SubProductAPI.getAllQuick(value).promise.then((subproducts) => {
                    setSubProductsMap((subProductsMap) => ({
                        ...subProductsMap,
                        [value]: subproducts
                    }));
                }).catch(err => console.error(err));
            }

            const envMapKey = `${newValues[name].product}-${value}`;
            if(type === 'subProduct' && !envMap[envMapKey]) {
                const subId = find(subProductsMap[newValues[name].product], (sub) => sub.name === value).id;
                fetchEnv(subId, envMapKey)
            }
        }

        form.setFieldsValue(newValues);
    };

    const configureTool = async (data) => {
        try {
        setLoading(true);
        const { configurations } = data;
        const newConfigurations = configurations.map((config, index) => {
            let { product, subProduct, environment, id } = config;
            if(hasDisabledEnvironment){
                environment = envValue;
            }
            const _product = find(products, (obj) => obj.id === product);
            //sending index to handle multiple mapping case
            //so that we can access and identify individual configuration
            const extraFieldsData = isFunction(props.getExtraFieldsData) ? props.getExtraFieldsData(config, index) : {};
            return {
                ...extraFieldsData,
                product_name: _product.name,
                sub_product_name: subProduct,
                environment,
                tool_name: toolName,
                login_id: loginConfigId,
                toolType: IntegrationType.PULL,
                id
            }
        })
        //send POST request when new mapping else, PUT request 
            if(editMode && !props.isUnmapped) {
                await ToolsIntegrationsAPI.updateGenericConfigurationByToolName(newConfigurations[0]).promise;
            } else{
                await ToolsIntegrationsAPI.postGenericToolsConfiguration(newConfigurations).promise;
            }
            message.success('Configuration saved successfully');
            afterClose();
            onUpdateConfigComponent && onUpdateConfigComponent();
        } catch (e) {
            setLoading(false);
            throw e;
        }
    };

    return (
        <BaseModalForm
            ref={ref}
            form={form}
            title={props.title || `Map ${toolName} Projects`}
            modalProps={modalProps}
            onOk={configureTool}
            afterClose={afterClose}
            initialValues={configurations}
            loading={toolLoading || loading}
            onValuesChange={onValuesChange}
            fields={[
                {
                name: "configurations",
                form: form,
                type: FieldTypes.LIST,
                newRowDefaultValue: props.newRowDefaultValue,
                editMode: editMode,
                addButtonDisabled: disableAddConfiguration,
                deleteIconTooltip,
                fieldRows: [
                    [
                        ...(isArray(props.extraFields) ? props.extraFields : []),
                    ],
                    [
                        {
                            name: 'product',
                            type: FieldTypes.PROD_SUBPROD_SELECT_ADD,
                            colSpan: 8,
                            label: <>ArmorCode <Vocabulary name="product" isPlural={true}/></>,
                            props: {
                                getFormListOptions: () => {
                                    return products.map(item => ({label: item.name, value: item.id}))
                                },
                                onChange: (value, field) => onChange(value, field, 'product'),
                                dropdownType: 'product',
                                showAddButton: true,
                                dontFetchProductSubProduct: true,
                                onCreate: (createdProduct, field) => {
                                    onProductSubProductCreation(createdProduct, field, 'product')
                                }
                            },
                            rules: [{ required: true, message: <><Vocabulary name="product" isPlural={true}/> is required</> }],
                            disabled: disableMapping
                        },
                        {
                            name: 'subProduct',
                            type: FieldTypes.PROD_SUBPROD_SELECT_ADD,
                            colSpan: 8,
                            label: <>ArmorCode <Vocabulary name="subProduct" isPlural={true}/></>,
                            props: {
                                getFormListOptions: (fieldKey) => {
                                    const selectedProduct = form.getFieldValue("configurations")[fieldKey]?.product;
                                    return findOptionsFromMap(selectedProduct, subProductsMap);
                                },
                                getProductId: (listIndex) => {
                                    const configData = form.getFieldValue("configurations")
                                    return configData[listIndex]?.product
                                },
                                dontFetchProductSubProduct: true,
                                dropdownType: 'subProduct',
                                onCreate: (createdSubproduct, field) => {
                                    onProductSubProductCreation(createdSubproduct, field, 'subProduct')
                                },
                                onChange: (value, field) => onChange(value, field, 'subProduct')
                            },
                            rules: [{ required: true, message: <><Vocabulary name="subProduct" isPlural={true}/> is required</> }],
                            disabled: disableMapping
                        },
                        ...(hasDisabledEnvironment ? [] : [{
                            name: 'environment',
                            type: 'Select',
                            colSpan: 7,
                            label: 'ArmorCode Environment',
                            props: {
                                getFormListOptions: (fieldKey) => {
                                    const selectedProduct = form.getFieldValue("configurations")[fieldKey]?.product;
                                    const selectedSubProduct = form.getFieldValue("configurations")[fieldKey]?.subProduct;
                                    return findOptionsFromMap(`${selectedProduct}-${selectedSubProduct}`, envMap, true);
                                },
                            },
                            rules: [{ required: true, message: 'Environment is required' }],
                            disabled: disableMapping
                        }])
                    ]
                ],
            }]}
        >
        </BaseModalForm>
    )
});

export const getSelectOptions = (array) => array.map(option => ({ label: option.name, value: option.name }));

export const findOptionsFromMap = (key, source, isEnvField = false) => {
    const data = source[key];
    if (!data) {
       return [];
    } 
    return isEnvField ? getEnvSelectOptions(data) : getSelectOptions(data);
};

export default GenericMappingForm;
